import { type FC, lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { _isNil, _isNotEmpty, _notNil } from '@/littledash';

import type { Cage, CageApiId } from '@/model/Cage.model';
import type { ID } from '@/model/Common.model';
import type { MetadataField, MetadataFieldValueTypes } from '@/model/Metadata.model';
import { MetadataTypes } from '@/model/Metadata.model';

import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { createMetaMap } from '@/support/hot';
import { createMetaV16 } from '@/support/hot-v16';
import { web as webRoute } from '@/support/route';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { clearSessionStorage, successToast } from '@/helpers';
import { featuresSelector } from '@/support/Selectors';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import NoDataCard from '@/components/NoDataCard';
import Button from '@/components/UI/Button';
import Header from '@/components/UI/Header';
import Link from '@/components/UI/Link';
import SubHeader from '@/components/UI/SubHeader';

// @ts-expect-error — v12 @handsontable/react has no type declarations
import type HotTable from '@handsontable/react';

import { getMetaColumns, getMetaData, initCageSettings, initialBulkCageData } from '../Colony.utils';
import { initBulkCageSettingsV16, initialBulkCageDataV16 } from '../Colony.utils.v16';
import '../Colony.scss';

const SpreadSheet = lazy(() => import('@/components/UI/SpreadSheet'));
const BulkCagesTableV16 = lazy(() => import('./BulkCagesTableV16'));

interface BulkCageRow {
  id: ID;
  catalog: string;
  name: string;
  population: number;
  meta: Record<string, MetadataFieldValueTypes>;
}

interface CageUpdatePayload {
  api_id: CageApiId;
  name: string;
  meta: Record<string, MetadataFieldValueTypes>;
}

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
}));

const BulkCageEdit: FC = () => {
  const navigate = useNavigate();
  const { hotUpgrade } = useSelector(hotUpgradeSelector);

  const editCageRef = useRef<HotTable>(null);
  const refV16 = useRef<HotTableRef>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<Array<MetadataField> | false>(false);
  const [metaColumns, setMetaColumns] = useState<Array<MetadataField> | false>(false);
  const [data, setData] = useState<Array<BulkCageRow>>([]);
  const [settings, setSettings] = useState<CustomGridSettings | SpreadsheetV16Settings | null>(null);
  const [apiError, setApiError] = useState<Error | false>(false);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  const selectedCages: Array<Cage> = useMemo(
    () => JSON.parse(sessionStorage.getItem('selectedColonyCages') ?? '[]'),
    []
  );

  const { invoke: updateCages } = useApiHook({
    endpoint: 'PATCH /api/v1/cages',
    options: {
      invokeOnInit: false,
      onError: { throw: true, capture: true, toast: false, slug: 'bulk-cages-update' },
    },
  });

  useEffect(() => {
    return clearSessionStorage('selectedColonyCages', '/colony/edit/cages');
  }, []);

  const fetchMetadata = async (): Promise<void> => {
    setLoading(true);
    try {
      const metadataResponse = await getMetaData(MetadataTypes.collection_meta);
      const responseData = (metadataResponse?.body ?? []) as Array<MetadataField>;
      setMetadata(responseData);
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetadata();
    if (hotUpgrade) {
      setData(initialBulkCageDataV16(selectedCages) as Array<BulkCageRow>);
    } else {
      setData(initialBulkCageData(selectedCages));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotUpgrade]);

  useEffect(() => {
    if (metaColumns === false && _isNotEmpty(data)) {
      if (metadata !== false && _isNotEmpty(metadata)) {
        setMetaColumns(getMetaColumns(selectedCages, metadata));
      } else {
        setMetaColumns([]);
      }
    }
  }, [metadata]);

  useEffect(() => {
    if (Array.isArray(metaColumns) && _isNil(settings)) {
      const metadataArray = metadata !== false ? metadata : [];

      if (hotUpgrade) {
        const updateSettings = initBulkCageSettingsV16(metaColumns, metadataArray);
        if (_notNil(updateSettings.settings)) {
          setSettings(updateSettings.settings);
        }
        if (_notNil(updateSettings.metadata)) {
          setMetadata(updateSettings.metadata);
        }
      } else {
        const updateSettings = initCageSettings(true, false, metaColumns, metadataArray) as {
          settings?: CustomGridSettings;
          metadata?: Array<MetadataField>;
        };
        if (_notNil(updateSettings.settings)) {
          setSettings(updateSettings.settings);
        }
        if (_notNil(updateSettings.metadata)) {
          setMetadata(updateSettings.metadata);
        }
      }
    }
  }, [metaColumns]);

  const putCages = async (cages: Array<CageUpdatePayload>): Promise<void> => {
    if (_isNotEmpty(cages)) {
      await updateCages({ body: { cages } });
    }
  };

  const bulkCagesSave = async (): Promise<void> => {
    if (isSaving || _isNil(settings)) {
      return;
    }

    setIsSaving(true);

    // The v1 endpoint identifies each cage by its api_id, whereas the spreadsheet keys rows by the
    // numeric cage id, so build a lookup to translate the cage id to its api_id. Metadata, however,
    // is sent as a flat map keyed by the numeric glossary id, matching the animals bulk-update
    // contract.
    const cageApiIdById = new Map<ID, CageApiId>(selectedCages.map((cage) => [cage.id, cage.api_id]));

    const cages: Array<CageUpdatePayload> = data.flatMap(({ id, name, meta }) => {
      const apiId = cageApiIdById.get(id);
      if (_isNil(apiId)) {
        return [];
      }

      let metaPayload: Record<string, MetadataFieldValueTypes> = {};
      if (_isNotEmpty(meta)) {
        if (hotUpgrade) {
          const v16Settings = settings as SpreadsheetV16Settings;
          const metaArray = createMetaV16(meta, v16Settings.columns ?? []);
          metaPayload = metaArray.reduce<Record<string, MetadataFieldValueTypes>>((acc, item) => {
            acc[String(item.glossary_id)] = item.value;
            return acc;
          }, {});
        } else {
          metaPayload = createMetaMap(meta, (settings as CustomGridSettings).columns);
        }
      }

      return [{ api_id: apiId, name, meta: metaPayload }];
    });
    try {
      await putCages(cages);
      navigate('/colony');
      successToast(`Successfully edited ${cages.length > 1 ? 'cages' : 'cage'}`);
    } catch (error) {
      setApiError(error as Error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetMetadata = (newMetadata: Array<MetadataField>): void => {
    setMetadata(newMetadata);
  };

  const spreadSheet = _notNil(settings) && (
    <>
      {hotUpgrade ? (
        <Suspense fallback={<Loading />}>
          <div className="ow-spreadsheet-styles">
            <BulkCagesTableV16
              data={data as unknown as Array<Record<string, unknown>>}
              settings={settings as SpreadsheetV16Settings}
              innerRef={refV16 as React.RefObject<HotTableRef>}
              metadata={metadata !== false ? metadata : []}
              setMetadata={handleSetMetadata}
              setInvalid={setIsInvalid}
              maxRows={data.length}
            />
          </div>
        </Suspense>
      ) : (
        <Suspense fallback={<Loading />}>
          <div className="ow-spreadsheet-styles">
            <SpreadSheet
              data={data}
              settings={settings as CustomGridSettings}
              className="pl4 pr4"
              innerRef={editCageRef}
              metadata={metadata !== false ? metadata : undefined}
              setMetadata={handleSetMetadata}
              maxRows={data.length}
              setInvalid={setIsInvalid}
            />
          </div>
        </Suspense>
      )}
      <div className="pl4 pt4">
        <Button
          testId="bulk-cages__save"
          onClick={bulkCagesSave}
          disabled={loading || isInvalid || isSaving}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
        <Button plain className="ml3" onClick={() => navigate('/colony')}>
          Cancel
        </Button>
      </div>
    </>
  );

  return (
    <div className="h-100">
      {apiError !== false && (
        <ApiErrorBanner
          className="mb4"
          title={'There was an error with your request'}
          text={
            'An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support.'
          }
          error={apiError}
        />
      )}
      {loading ? (
        <Loading />
      ) : (
        <>
          <SubHeader linkToText="Colony" link={webRoute('colony')} />
          <div className="ph4">
            <Header mainHeaderText="Edit cages" />
          </div>
          {_isNotEmpty(selectedCages) ? (
            spreadSheet
          ) : (
            <div className="ma4">
              <NoDataCard
                title="You have not selected any cages"
                text={'Please select cages from inside Colony to proceed'}
                NoDataComponent={
                  <Link to={webRoute('colony')} className="link blue">
                    Go to Colony
                  </Link>
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BulkCageEdit;
